import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getTournaments, getTournamentEvents, getFinancialSummary, getPlayersByTournament, getPartnersByTournament } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import TournamentDetailsComponent from '../components/TournamentDetails';
import TournamentModal from '../components/TournamentModal';
import PressCoverageManager from './dashboard/PressCoverageManager';
import TournamentCategoriesRounds from '../components/TournamentCategoriesRounds';
import SpecialPrizesManager from '../components/SpecialPrizesManager';
import { format } from 'date-fns';
import {
  UserGroupIcon,
  BanknotesIcon,
  CalendarIcon,
  ChartBarIcon,
  ArrowLeftIcon,
  NewspaperIcon,
  TagIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import CountUp from 'react-countup';
import EventList from '../components/EventList';

export default function TournamentDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    players: {
      total: 0,
      registered: 0,
      pending: 0
    },
    events: {
      total: 0,
      upcoming: 0,
      completed: 0
    },
    finances: {
      revenue: 0,
      expenses: 0,
      balance: 0
    }
  });
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (id) {
      fetchTournament();
    }
  }, [id]);

  const fetchTournament = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch tournament details
      const { data: tournaments, error: tournamentError } = await getTournaments();
      
      if (tournamentError) throw tournamentError;
      
      const foundTournament = tournaments?.find(t => t.id === id);
      
      if (!foundTournament) {
        throw new Error('Tournament not found');
      }
      
      setTournament(foundTournament);
      
      // Fetch tournament stats
      await fetchTournamentStats(foundTournament.id);
      
    } catch (err) {
      console.error('Error fetching tournament:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tournament');
      toast.error('Failed to load tournament details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTournamentStats = async (tournamentId: string) => {
    try {
      // Fetch players
      const { data: playersData, error: playersError } = await getPlayersByTournament(tournamentId);
      
      if (playersError) throw playersError;
      
      // Fetch events
      const { data: eventsData, error: eventsError } = await getTournamentEvents(tournamentId);
      
      if (eventsError) throw eventsError;
      
      // Fetch financial summary
      const { data: financialData, error: financialError } = await getFinancialSummary(tournamentId);
      
      if (financialError && !financialError.message.includes('No rows found')) throw financialError;
      
      // Calculate player stats
      const totalPlayers = playersData?.length || 0;
      const registeredPlayers = playersData?.filter(p => p.status === 'Active').length || 0;
      const pendingPlayers = playersData?.filter(p => p.status === 'Pending').length || 0;
      
      // Calculate event stats
      const now = new Date();
      const totalEvents = eventsData?.length || 0;
      const upcomingEvents = eventsData?.filter(e => new Date(e.date) > now).length || 0;
      const completedEvents = eventsData?.filter(e => new Date(e.date) <= now).length || 0;
      
      // Calculate financial stats
      const revenue = financialData?.total_revenue || 0;
      const expenses = financialData?.total_expenses || 0;
      const balance = revenue - expenses;
      
      setStats({
        players: {
          total: totalPlayers,
          registered: registeredPlayers,
          pending: pendingPlayers
        },
        events: {
          total: totalEvents,
          upcoming: upcomingEvents,
          completed: completedEvents
        },
        finances: {
          revenue,
          expenses,
          balance
        }
      });
      
    } catch (err) {
      console.error('Error fetching tournament stats:', err);
      toast.error('Failed to load tournament statistics');
    }
  };

  const handleEditTournament = () => {
    setIsEditModalOpen(true);
  };

  const handleBack = () => {
    navigate('/dashboard/tournaments');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-lg mx-auto bg-white rounded-xl shadow-md p-8 text-center">
          <h2 className="text-2xl font-bold text-golf-gray mb-4">Tournament Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The tournament you are looking for does not exist or has been removed.'}</p>
          <button
            onClick={handleBack}
            className="modern-button px-6 py-3 text-white"
          >
            Back to Tournaments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-golf-orange hover:text-golf-green transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span>Back to Tournaments</span>
        </button>
      </div>

      {/* Tournament Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <h1 className="text-3xl font-bold text-golf-gray">{tournament.name}</h1>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${
                tournament.status === 'upcoming'
                  ? 'bg-blue-100 text-blue-800'
                  : tournament.status === 'ongoing'
                  ? 'bg-green-100 text-green-800'
                  : tournament.status === 'completed'
                  ? 'bg-gray-100 text-gray-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {tournament.status}
              </span>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                <span>
                  {format(new Date(tournament.start_date), 'MMMM d')} - {format(new Date(tournament.end_date), 'MMMM d, yyyy')}
                </span>
              </div>
              {tournament.venue && (
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{tournament.venue}</span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleEditTournament}
            className="modern-button px-4 py-2 text-white font-medium"
          >
            Edit Tournament
          </button>
        </div>

        {tournament.description && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-golf-gray mb-2">Description</h2>
            <p className="text-gray-600">{tournament.description}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <UserGroupIcon className="w-5 h-5 text-blue-600" />
              <h3 className="font-medium text-golf-gray">Capacity</h3>
            </div>
            <p className="text-2xl font-bold text-golf-gray">{tournament.capacity} <span className="text-sm font-normal text-gray-500">Players</span></p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <BanknotesIcon className="w-5 h-5 text-green-600" />
              <h3 className="font-medium text-golf-gray">Registration Fee</h3>
            </div>
            <p className="text-2xl font-bold text-golf-gray">{formatCurrency(tournament.registration_fee)}</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <BanknotesIcon className="w-5 h-5 text-purple-600" />
              <h3 className="font-medium text-golf-gray">Prize Pool</h3>
            </div>
            <p className="text-2xl font-bold text-golf-gray">{formatCurrency(tournament.prize_pool)}</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
        <div className="flex overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 py-4 px-6 text-center font-medium border-b-2 ${
              activeTab === 'overview'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-golf-gray hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`flex-1 py-4 px-6 text-center font-medium border-b-2 ${
              activeTab === 'events'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-golf-gray hover:border-gray-300'
            }`}
          >
            Events
          </button>
          <button
            onClick={() => setActiveTab('players')}
            className={`flex-1 py-4 px-6 text-center font-medium border-b-2 ${
              activeTab === 'players'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-golf-gray hover:border-gray-300'
            }`}
          >
            Players
          </button>
          <button
            onClick={() => setActiveTab('finances')}
            className={`flex-1 py-4 px-6 text-center font-medium border-b-2 ${
              activeTab === 'finances'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-golf-gray hover:border-gray-300'
            }`}
          >
            Finances
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`flex-1 py-4 px-6 text-center font-medium border-b-2 flex items-center justify-center gap-2 ${
              activeTab === 'categories'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-golf-gray hover:border-gray-300'
            }`}
          >
            <TagIcon className="w-4 h-4" />
            Categories & Rounds
          </button>
          <button
            onClick={() => setActiveTab('prizes')}
            className={`flex-1 py-4 px-6 text-center font-medium border-b-2 flex items-center justify-center gap-2 ${
              activeTab === 'prizes'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-golf-gray hover:border-gray-300'
            }`}
          >
            <TrophyIcon className="w-4 h-4" />
            Special Prizes
          </button>
          <button
            onClick={() => setActiveTab('press')}
            className={`flex-1 py-4 px-6 text-center font-medium border-b-2 flex items-center justify-center gap-2 ${
              activeTab === 'press'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-golf-gray hover:border-gray-300'
            }`}
          >
            <NewspaperIcon className="w-4 h-4" />
            Press
          </button>
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Players Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-blue-500/10 p-3 rounded-xl">
                  <UserGroupIcon className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Players</p>
                  <div className="flex items-center gap-2">
                    <CountUp
                      end={stats.players.total}
                      duration={2}
                      className="text-2xl font-bold text-golf-gray"
                    />
                    <span className="text-sm text-gray-500">
                      ({stats.players.registered} registered)
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm text-gray-500">Pending</div>
                  <div className="text-lg font-semibold text-golf-gray">{stats.players.pending}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm text-gray-500">Capacity</div>
                  <div className="text-lg font-semibold text-golf-gray">{tournament.capacity}</div>
                </div>
              </div>
            </motion.div>

            {/* Events Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-orange-500/10 p-3 rounded-xl">
                  <CalendarIcon className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Events</p>
                  <div className="flex items-center gap-2">
                    <CountUp
                      end={stats.events.total}
                      duration={2}
                      className="text-2xl font-bold text-golf-gray"
                    />
                    <span className="text-sm text-gray-500">
                      total
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm text-gray-500">Upcoming</div>
                  <div className="text-lg font-semibold text-golf-gray">{stats.events.upcoming}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm text-gray-500">Completed</div>
                  <div className="text-lg font-semibold text-golf-gray">{stats.events.completed}</div>
                </div>
              </div>
            </motion.div>

            {/* Revenue Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-green-500/10 p-3 rounded-xl">
                  <BanknotesIcon className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Revenue</p>
                  <div className="flex items-center gap-2">
                    <CountUp
                      end={stats.finances.revenue}
                      duration={2}
                      formattingFn={(value) => formatCurrency(value)}
                      className="text-2xl font-bold text-golf-gray"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Progress</span>
                  <span className="text-gray-500">
                    {((stats.finances.revenue / tournament.prize_pool) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 rounded-full h-2"
                    style={{
                      width: `${Math.min((stats.finances.revenue / tournament.prize_pool) * 100, 100)}%`
                    }}
                  />
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  Target: {formatCurrency(tournament.prize_pool)}
                </div>
              </div>
            </motion.div>

            {/* Expenses Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-red-500/10 p-3 rounded-xl">
                  <ChartBarIcon className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Expenses</p>
                  <div className="flex items-center gap-2">
                    <CountUp
                      end={stats.finances.expenses}
                      duration={2}
                      formattingFn={(value) => formatCurrency(value)}
                      className="text-2xl font-bold text-golf-gray"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm text-gray-500">Net Balance</div>
                  <div className={`text-lg font-semibold ${stats.finances.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(stats.finances.balance)}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Tournament Details Component */}
          <TournamentDetailsComponent 
            tournament={tournament} 
            onBack={() => {}} 
            onEdit={handleEditTournament}
          />
        </div>
      )}

      {activeTab === 'events' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-golf-gray mb-6">Tournament Events</h2>
          <EventList tournamentId={tournament.id} />
        </div>
      )}

      {activeTab === 'players' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-golf-gray mb-6">Registered Players</h2>
          <p className="text-gray-600">
            Total Players: {stats.players.total} | 
            Registered: {stats.players.registered} | 
            Pending: {stats.players.pending}
          </p>
          <div className="mt-4">
            <button
              onClick={() => navigate(`/dashboard/players`)}
              className="modern-button px-4 py-2 text-white font-medium"
            >
              Manage Players
            </button>
          </div>
        </div>
      )}

      {activeTab === 'finances' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-golf-gray mb-6">Financial Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Total Revenue</h3>
              <p className="text-2xl font-bold text-golf-gray">{formatCurrency(stats.finances.revenue)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Total Expenses</h3>
              <p className="text-2xl font-bold text-golf-gray">{formatCurrency(stats.finances.expenses)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Net Balance</h3>
              <p className={`text-2xl font-bold ${stats.finances.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(stats.finances.balance)}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={() => navigate(`/dashboard/financials?tournamentId=${tournament.id}`)}
              className="modern-button px-4 py-2 text-white font-medium"
            >
              View Financial Details
            </button>
          </div>
        </div>
      )}

      {activeTab === 'categories' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-golf-gray">Categories & Golf Rounds</h2>
            <p className="text-sm text-gray-500 mt-1">Define player categories and the golf rounds for this tournament.</p>
          </div>
          <TournamentCategoriesRounds tournamentId={tournament.id} />
        </div>
      )}

      {activeTab === 'prizes' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-golf-gray">Special Prizes</h2>
            <p className="text-sm text-gray-500 mt-1">
              Record winners for Nearest to the Pin, Longest Drive, and Hole-in-One prizes per round.
            </p>
          </div>
          <SpecialPrizesManager tournamentId={tournament.id} />
        </div>
      )}

      {activeTab === 'press' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <PressCoverageManager tournamentId={tournament.id} />
        </div>
      )}

      {/* Edit Tournament Modal */}
      <TournamentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        tournament={tournament}
        onSuccess={() => {
          setIsEditModalOpen(false);
          fetchTournament();
        }}
      />
    </div>
  );
}